import { useEffect, useRef } from 'react';

/** Everything the review shortcuts can do. Values are read live, so this may be rebuilt freely. */
export interface ReviewKeyActions {
  frame: number;
  frameCount: number;
  seek: (frame: number) => void;
  togglePlay: () => void;
  stepShot: (delta: number) => void;
  /** Halve (0.5) or double (2) the playback rate. */
  changeRate: (factor: number) => void;
  toggleApprove: () => void;
  toggleReject: () => void;
  toggleDraw: () => void;
  toggleWipe: () => void;
  /** Collapse/restore the side panels so the clip fills the screen. */
  toggleFocus: () => void;
  undo: () => void;
  /** False while a dialog owns the screen — then the shortcuts stay out of the way. */
  enabled: boolean;
}

/**
 * The review keyboard. The actions are routed through a ref so the listener is registered
 * exactly once: it closes over live values instead of being torn down and rebuilt on every
 * frame update.
 */
export function useReviewKeys(actions: ReviewKeyActions): void {
  const ref = useRef(actions);
  ref.current = actions;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      // Never steal keys from a text field.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const a = ref.current;
      if (!a.enabled) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          a.togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          a.seek(a.frame - (e.shiftKey ? 10 : 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          a.seek(a.frame + (e.shiftKey ? 10 : 1));
          break;
        case 'Home':
          e.preventDefault();
          a.seek(0);
          break;
        case 'End':
          e.preventDefault();
          a.seek(a.frameCount - 1);
          break;
        case '[':
          e.preventDefault();
          a.stepShot(-1);
          break;
        case ']':
          e.preventDefault();
          a.stepShot(1);
          break;
        case 'j':
          a.changeRate(0.5);
          break;
        case 'k':
          a.togglePlay();
          break;
        case 'l':
          a.changeRate(2);
          break;
        case 'a':
          a.toggleApprove();
          break;
        case 'r':
          a.toggleReject();
          break;
        case 'd':
          a.toggleDraw();
          break;
        case 'f':
          e.preventDefault();
          a.toggleFocus();
          break;
        case 'w':
          a.toggleWipe();
          break;
        case 'z':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault();
            a.undo();
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
